import {COMMAND_TYPES} from '@github-ui/copilot-shared-ui/ahp/command-types'
import type {
  PermissionRequestArguments,
  NormalizedPrompt,
} from '@github-ui/copilot-shared-ui/ahp/interactive-prompt-types'
import type {Command, LogEntry} from '../types/session'
import {isPermissionRequestArguments} from '../types/session'
import {extractPromptFromEntry} from '../components/session-logs/prompt-log-utils'

const MAX_PREVIEW_VALUE_CHARS = 200

/**
 * Map raw permission-request fields onto the {@link PermissionRequestArguments}
 * shape the UI expects. Shared by the events/logs model builder and the AHP
 * relay source so both produce identical permission args from the CLI's
 * permission payload (`permissionRequest`/`promptRequest`), whether it arrives
 * as an event field or on an AHP tool call's `_meta`.
 */
export function normalizePermissionArguments(
  permReq: Record<string, unknown>,
  promptReq?: Record<string, unknown> | null,
): PermissionRequestArguments {
  return {
    kind: (promptReq?.kind as string) ?? (permReq.kind as string) ?? 'unknown',
    command: (permReq.fullCommandText as string | undefined) ?? (permReq.command as string | undefined),
    description: (permReq.intention as string | undefined) ?? (permReq.description as string | undefined),
    toolName: permReq.toolName as string | undefined,
    serverName: permReq.serverName as string | undefined,
    filePath: (permReq.filePath as string | undefined) ?? (permReq.fileName as string | undefined),
    diff: permReq.diff as string | undefined,
    canOfferSessionApproval:
      (promptReq?.canOfferSessionApproval as boolean | undefined) ??
      (permReq.canOfferSessionApproval as boolean | undefined),
    commandIdentifiers:
      (promptReq?.commandIdentifiers as string[] | undefined) ?? (permReq.commandIdentifiers as string[] | undefined),
    args: permReq.args as Record<string, unknown> | undefined,
    toolArgs: permReq.toolArgs as string | undefined,
  }
}

export interface PermissionLabelInfo {
  isCommand: boolean
  isWrite: boolean
  isMcp: boolean
  label: string
}

/** Derive display label and kind flags from permission request fields. */
export function getPermissionLabel(kind: string, toolName?: string, serverName?: string): PermissionLabelInfo {
  const isCommand = kind === 'commands' || kind === 'shell'
  const isWrite = kind === 'write'
  const isMcp = kind === 'mcp'

  const label = isCommand
    ? 'Run command'
    : isWrite
      ? 'Edit file'
      : isMcp
        ? `MCP tool: ${serverName ? `${serverName}/` : ''}${toolName ?? 'unknown'}`
        : `Permission: ${toolName ?? kind}`

  return {isCommand, isWrite, isMcp, label}
}

/**
 * Format an MCP tool call's args as a multi-line `key: value` preview.
 * Strings render verbatim (truncated past MAX_PREVIEW_VALUE_CHARS); other
 * shapes fall back to JSON.stringify so nested objects still render
 * something useful. Returns undefined when there's nothing meaningful to
 * preview, so the caller can collapse the preview slot entirely.
 */
export function formatMcpArgsForPreview(mcpArgs: Record<string, unknown> | undefined): string | undefined {
  if (!mcpArgs) return undefined
  const entries = Object.entries(mcpArgs)
  if (entries.length === 0) return undefined

  const lines: string[] = []
  for (const [key, value] of entries) {
    if (value === undefined || value === null) continue
    let str: string
    if (typeof value === 'string') {
      str = value
    } else {
      try {
        str = JSON.stringify(value)
      } catch {
        str = String(value)
      }
    }
    if (str.length > MAX_PREVIEW_VALUE_CHARS) {
      str = `${str.slice(0, MAX_PREVIEW_VALUE_CHARS)}…`
    }
    lines.push(`${key}: ${str}`)
  }
  return lines.length > 0 ? lines.join('\n') : undefined
}

/**
 * For a permission_response with `approved: true` and `scope: "session"`
 * on an MCP prompt, find every other pending permission_request in the
 * logs sharing the same (serverName, toolName) so the caller can
 * optimistically resolve them too. Mirrors the runtime's
 * `resumePendingPermissions` fan-out so the FIFO advances past sibling
 * prompts immediately on click.
 *
 * Returns an empty array for non-MCP prompts, scope="once" responses,
 * denials, or when there are no sibling prompts in the logs.
 */
export function findSessionFanoutTargets(
  pendingPrompt: NormalizedPrompt,
  response: unknown,
  logs: LogEntry[] | undefined,
  triggeringToolCallId: string,
): Array<{toolCallId: string}> {
  if (!logs?.length) return []
  const prompts: NormalizedPrompt[] = []
  for (const entry of logs) {
    const prompt = extractPromptFromEntry(entry)
    if (!prompt) continue
    prompts.push(prompt)
  }

  return findSessionFanoutTargetsFromPrompts(pendingPrompt, response, prompts, triggeringToolCallId)
}

/**
 * Extract prompt IDs resolved by persisted prompt-response commands. This keeps
 * prompt stacks stable across refreshes, when in-memory optimistic IDs are gone.
 * `failed` and `dismissed` commands are ignored so a failed submission doesn't
 * hide a still-pending prompt. Session-scoped MCP approvals also resolve sibling
 * prompts that the runtime fans out to the same (serverName, toolName).
 */
export function getPromptResponseResolvedPromptIds(
  commands: Command[] | undefined,
  prompts: readonly NormalizedPrompt[] | undefined,
): Set<string> {
  const ids = new Set<string>()

  for (const command of commands ?? []) {
    if (
      command.type !== COMMAND_TYPES.ASK_USER_RESPONSE &&
      command.type !== COMMAND_TYPES.ELICITATION_RESPONSE &&
      command.type !== COMMAND_TYPES.PLAN_APPROVAL_RESPONSE &&
      command.type !== COMMAND_TYPES.PERMISSION_RESPONSE
    ) {
      continue
    }
    if (command.state === 'failed' || command.state === 'dismissed') continue

    let parsed: {promptId?: unknown; approved?: unknown; scope?: unknown}
    try {
      parsed = JSON.parse(command.content) as {promptId?: unknown; approved?: unknown; scope?: unknown}
    } catch {
      continue
    }

    if (!parsed || typeof parsed !== 'object') continue
    if (typeof parsed.promptId !== 'string') continue
    ids.add(parsed.promptId)

    if (command.type !== COMMAND_TYPES.PERMISSION_RESPONSE || parsed.approved !== true || parsed.scope !== 'session') {
      continue
    }

    if (!prompts?.length) continue
    const triggeringPrompt = prompts.find(prompt => prompt.toolCallId === parsed.promptId)
    if (!triggeringPrompt) continue

    for (const target of findSessionFanoutTargetsFromPrompts(triggeringPrompt, parsed, prompts, parsed.promptId)) {
      ids.add(target.toolCallId)
    }
  }

  return ids
}

export function getResolvedPromptIdsFromCommands(
  commands: Command[] | undefined,
  logs: LogEntry[] | undefined,
): Set<string> {
  const prompts = logs?.flatMap(entry => {
    const prompt = extractPromptFromEntry(entry)
    return prompt ? [prompt] : []
  })
  return getPromptResponseResolvedPromptIds(commands, prompts)
}

function findSessionFanoutTargetsFromPrompts(
  pendingPrompt: NormalizedPrompt,
  response: unknown,
  prompts: readonly NormalizedPrompt[],
  triggeringToolCallId: string,
): Array<{toolCallId: string}> {
  if (!isPermissionRequestArguments(pendingPrompt.arguments)) return []
  if (pendingPrompt.arguments.kind !== 'mcp') return []
  const {serverName, toolName} = pendingPrompt.arguments
  if (!serverName || !toolName) return []

  const respObj = response as {approved?: unknown; scope?: unknown} | null
  if (!respObj || respObj.approved !== true || respObj.scope !== 'session') return []

  const targets: Array<{toolCallId: string}> = []
  for (const prompt of prompts) {
    if (prompt.toolCallId === triggeringToolCallId) continue
    if (prompt.toolName !== 'permission_request') continue
    if (!isPermissionRequestArguments(prompt.arguments)) continue
    if (prompt.arguments.kind !== 'mcp') continue
    if (prompt.arguments.serverName !== serverName) continue
    if (prompt.arguments.toolName !== toolName) continue
    targets.push({toolCallId: prompt.toolCallId})
  }
  return targets
}
